import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useFavouritesQuery, useLogMutation } from '@gql/schema';
import { ISaleLabelProps, UserContext } from '@secretescapes/cauldron';
import {
  BadgeType,
  GalleryElement,
  IconName,
  useDeviceViewport,
  useTheme,
} from '@secretescapes/falcon';
import * as Sentry from '@sentry/react';
import { EXPIRED_IMAGE_URL, IMAGE_DIMENSIONS, mapToGalleryElement } from '@utils/image.utils';
import { getFormattedPriceWithBookingFee } from '@utils/pricing.utils';
import { FAMILY_SALE_TAGS } from '@utils/tags.config';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import useConfig from '@utils/useConfig';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useServerState } from '@utils/useServerState';
import { useTranslations } from '@utils/useTranslations';
import { notEmpty } from '@features/AvailabilityPresentation/AvailabilityPresentation.utils';
import { SalePageDesktop, SalePageMobile } from '@features/Sale';
import { transformFacilities } from '@features/Sale/Facilities';
import { RetryBox } from '@features/Sale/RetryBox';
import { GlobalDesktopStyles } from './SalePage.styles';

const SalePage = () => {
  const { showPricingAndAvailability, pricingRules, warningMessageBody, showAds } = useAppState();
  const { sale } = useServerState();
  const theme = useTheme();
  const { isMobile, territoryLocale, currency, featureFlags } = useApplicationState();
  const deviceViewport = useDeviceViewport();
  const { userId } = useContext(UserContext);
  const { translations } = useTranslations();

  const SaleComponent = isMobile ? SalePageMobile : SalePageDesktop;

  const config = useConfig();
  const showReviews = useFeatureFlag(BullfinchFeatureFlags.reviewsEnabled);
  const flightsEnabled = useFeatureFlag(BullfinchFeatureFlags.flightsEnabled);
  const assistedSalesPromoEnabled = useFeatureFlag(BullfinchFeatureFlags.assistedSalesPromoEnabled);
  const expireImages = useFeatureFlag(BullfinchFeatureFlags.expireImagesEnabled);
  const bookingFeesEnabled = useFeatureFlag(BullfinchFeatureFlags.bookingFeesEnabled);

  const hasLogged = useRef(false);

  const { data: favouritesData, refetch: refetchFavourites } = useFavouritesQuery({
    ssr: false,
    skip: !userId,
    variables: { userId: userId! },
  });

  const {
    id,
    photos,
    prices,
    editorial,
    facilities,
    location,
    badges,
    attributes,
    dates,
    tags,
    travel,
    reviews,
    staticMapUrl,
  } = sale ?? {};

  const { discount, discountTooltip, leadRate } = prices ?? {};
  const {
    title,
    destinationName,
    weLike,
    hotelDetails,
    summary,
    location: mapAddress,
    mainParagraph,
  } = editorial ?? {};

  const endDateObject = new Date(dates?.end ?? '');
  const nowDateObject = new Date();
  const isExpiredSale = Number(nowDateObject) - Number(endDateObject) >= 0;

  const images: GalleryElement[] =
    expireImages && isExpiredSale
      ? [
          {
            title: '',
            imgUrl: EXPIRED_IMAGE_URL,
          },
        ]
      : (photos
          ?.filter(notEmpty)
          .map((photo, index) =>
            mapToGalleryElement(photo, index, IMAGE_DIMENSIONS[deviceViewport])
          ) ?? []);

  const LOWEST_PRIORITY = 9999;

  const badgesFormatted: ISaleLabelProps[] = (badges ?? []).map(data => ({
    backgroundColor: data?.backgroundColor?.light ?? '',
    fontColor: data?.fontColor?.light ?? '',
    icon: (data?.iconName ?? '') as IconName,
    type: (data?.type ?? BadgeType.INFORMATIONAL) as BadgeType,
    priority: data?.priority ?? LOWEST_PRIORITY,
    saleLabelTranslations: {
      translatedLabelValue: data?.title ?? '',
      translatedTooltipTitle: data?.tooltip?.title ?? '',
      translatedTooltipValue: data?.tooltip?.description ?? '',
    },
  }));

  const transformedFacilities = useMemo(
    () => transformFacilities(facilities, translations),
    [facilities, translations]
  );

  const flightPriceAvailable =
    flightsEnabled && !!travel?.hasFlightsAvailable && !!prices?.withFlights?.forDisplay;

  const isFamilySale = FAMILY_SALE_TAGS.some(element => tags?.includes(element));
  const showAssistedSaleBanner = assistedSalesPromoEnabled;

  const [addLog] = useLogMutation();

  useEffect(() => {
    // Logging for Price Elasticity Test
    if (!config.app.isLocal && !hasLogged.current) {
      hasLogged.current = true;
      const allFeatureFlags = [
        ...(window.SE?.tracyFeatureFlags ?? []),
        ...(window.SE?.cauldronFeatureFlags ?? []),
        ...featureFlags,
      ];
      const uniqueFeatureFlags = [...new Set(allFeatureFlags)];

      const snowplowTrackingCookie = new Map(
        document.cookie.split(';').map(cookie => {
          const [key, value] = cookie.split('=');
          return [key.trim(), value];
        })
      ).get('sp');

      const price = bookingFeesEnabled
        ? getFormattedPriceWithBookingFee(leadRate?.unit ?? 0, currency, territoryLocale)
        : (leadRate?.forDisplay ?? '');

      const logData = JSON.stringify({
        userId,
        snowplowTrackingCookie,
        featureFlags: uniqueFeatureFlags,
        price,
      });

      void addLog({ variables: { data: logData } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="SalePage">
      {!isMobile ? <GlobalDesktopStyles /> : null}
      <Sentry.ErrorBoundary
        beforeCapture={scope => scope.setTag('error.boundary', 'sale')}
        fallback={<RetryBox retryFunction={() => window?.location?.reload()} />}
      >
        <SaleComponent
          saleId={id ?? ''}
          maintenance={sale?.maintenance}
          images={images}
          staticMapSrc={staticMapUrl ?? ''}
          googleMapsKey={config?.app.GOOGLE_MAPS_API_KEY ?? ''}
          saleTitle={title ?? ''}
          destinationName={destinationName ?? ''}
          location={location}
          mapAddress={mapAddress ?? ''}
          hotelDetails={hotelDetails ?? ''}
          mainParagraph={mainParagraph ?? ''}
          summary={summary ?? ''}
          highlightsText={weLike ?? ''}
          badges={badgesFormatted}
          facilities={transformedFacilities}
          isFavourite={(favouritesData?.favorites ?? []).includes(id)}
          discount={`${discount}`}
          discountTooltip={discountTooltip ?? ''}
          pricingRules={pricingRules}
          showPricingAndAvailability={showPricingAndAvailability}
          isTimeLimited={!!attributes?.isTimeLimited}
          endDate={endDateObject}
          theme={theme}
          locale={territoryLocale}
          warningMessageBody={warningMessageBody}
          showAdvertisements={showAds}
          flightPriceAvailable={flightPriceAvailable}
          showReviews={showReviews}
          reviews={reviews}
          refetchFavourites={() => void refetchFavourites()}
          showAssistedSaleBanner={showAssistedSaleBanner}
          isFamilySale={isFamilySale}
          isExpiredSale={isExpiredSale}
        />
      </Sentry.ErrorBoundary>
    </div>
  );
};

export default SalePage;
